import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../utils/cn';

/**
 * Button variant styles
 *
 * Available variants:
 * - `primary` - Blue background, white text (default)
 * - `secondary` - Gray background
 * - `outline` - White background with border
 * - `ghost` - Transparent background, hover shows gray
 * - `danger` - Red background for destructive actions
 * - `warning` - Amber/orange background for warning actions
 * - `link` - Text only with underline on hover
 *
 * Available sizes:
 * - `sm` - Small (h-8)
 * - `md` - Medium (h-10, default)
 * - `lg` - Large (h-12)
 * - `icon` - Square icon button (h-10 w-10)
 */
/**
 * Button variant styles
 *
 * GAP-006: Touch target sizing
 * - Mobile/Tablet: minimum 44px × 44px (WCAG 2.1 AAA)
 * - Desktop: 32px minimum acceptable
 * - All sizes use min-h-[44px] on mobile via responsive classes
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mv-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-mv-blue-600 text-white hover:bg-mv-blue-900',
        primary: 'bg-mv-blue-600 text-white hover:bg-mv-blue-900',
        secondary: 'bg-mv-gray-100 text-mv-gray-700 hover:bg-mv-gray-200',
        outline: 'border border-mv-gray-300 bg-white text-mv-gray-700 hover:bg-mv-gray-50',
        ghost: 'text-mv-gray-700 hover:bg-mv-gray-100',
        danger: 'bg-mv-error-500 text-white hover:bg-mv-error-700',
        destructive: 'bg-mv-error-500 text-white hover:bg-mv-error-700',
        warning: 'bg-amber-500 text-white hover:bg-amber-600',
        link: 'text-mv-blue-600 underline-offset-4 hover:underline',
      },
      size: {
        // GAP-006: Mobile-first touch targets (44px min on mobile, can be smaller on desktop)
        small: 'min-h-[44px] px-3 text-xs md:min-h-[32px] md:h-8',
        sm: 'min-h-[44px] px-3 text-xs md:min-h-[32px] md:h-8',
        md: 'min-h-[44px] px-4 md:h-10',
        lg: 'min-h-[44px] px-6 text-base md:h-12',
        icon: 'min-h-[44px] min-w-[44px] md:h-10 md:w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  /** Alias for isLoading */
  loading?: boolean;
  /** Make button full width */
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading, loading, fullWidth, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const showLoading = isLoading ?? loading;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), fullWidth && 'w-full', className)}
        ref={ref}
        disabled={disabled || showLoading}
        {...props}
      >
        {showLoading ? (
          <>
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Loading...</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { buttonVariants };
